import { type SlotGameStore, type BaseComponentConfig, type Spine, type Sprite, type Particles } from "@gamzix/slot_core";

import { GameDataStore } from "../../../src/GameDataStore";
import { CONST } from "../../../src/const";
import { GameUtilsCustom } from "../../../utils/gameUtilsCustom/src";
import { AnimationHelpers } from "./AnimationHelpers";
import { HeapFeatureDataStore } from "./HeapFeatureDataStore";

class HeapFeature extends Game.src.component.BaseComponent {
    
    protected currentNameAnimation = null;
    
    protected symbolsFlyOutOfHeapOfStar = [];
    protected isShowing = true;

    protected reelSetWrapperContainerElement;
    protected reelFrameWrapperContainerElement;
    protected logoWrapperContainerElement;
    protected containerCounterSpriteElement;
    protected logoSpineContainerElement;
    protected fallingStarsSpineElement;
    protected containerJackpotPanelElement;
    protected heapOfStarsSpineElement;
    protected effectElements;

    protected reelset;

    static events = {
        FLY_FINISH_INTO_COUNTER: "HeapFeature.FLY_FINISH_INTO_COUNTER",
        FLY_START_INTO_COUNTER: "HeapFeature.FLY_START_INTO_COUNTER",
        FLY_FINISH_LAST_INTRO_COUNTER: "HeapFeature.FLY_FINISH_LAST_INTRO_COUNTER",
        FLY_START_LAST_INTRO_COUNTER: "HeapFeature.FLY_START_LAST_INTRO_COUNTER",
        FLY_FINISH_OUTRO_COUNTER: "HeapFeature.FLY_FINISH_OUTRO_COUNTER",
        FLY_START_OUTRO_COUNTER: "HeapFeature.FLY_START_OUTRO_COUNTER",
        FLY_START_LAST_OUTRO_COUNTER: "HeapFeature.FLY_START_LAST_OUTRO_COUNTER",
        TRIGGER_BONUS_GAME: "HeapFeature.TRIGGER_BONUS_GAME"
    };


    constructor(config: BaseComponentConfig) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isShowing: () => this.isShowing
        };
    }

    onGameReady() {
        super.onGameReady();

        this.initVariables();
        this.setCurrentLevelSprite(this.heapOfStarLevelValue);
    }

    initVariables() {
        this.containerCounterSpriteElement = game.tree.findByName("heapFeature.container_counter_sprite");
        this.reelSetWrapperContainerElement = game.tree.findByName("reel_set.wrapper_container");
        this.reelFrameWrapperContainerElement = game.tree.findByName("reel_frame.wrapper_container");
        this.logoWrapperContainerElement = game.tree.findByName("logo_wrapper_container");
        this.containerJackpotPanelElement = game.tree.findByName("jackpot_panel.wrapper_container");
        this.logoSpineContainerElement = game.tree.findByName("logo_wrapper_container.logo_spine_container");
        this.fallingStarsSpineElement = game.tree.findByName("falling_stars.falling_stars_spine");
        this.heapOfStarsSpineElement = game.tree.findByName("heapFeature.heap_of_stars_spine");
        this.effectElements = [
            this.reelSetWrapperContainerElement,
            this.reelFrameWrapperContainerElement,
            this.logoWrapperContainerElement,
            this.containerCounterSpriteElement,
            this.containerJackpotPanelElement,
            this.logoSpineContainerElement,
            this.heapOfStarsSpineElement
        ]

        this.reelset = game.component.reelset;
        this.setVariableCurrentLevel();
    }

    handleSymbolStartReplace() {
        this.startSymbolsFlyOutroHeapAnimation();
    }

    handleFlyFinishIntoCounter() {
        this.removeSpriteLevel()

        if (this.store.gameData.bonusGame.isPlaying()) return;

        AnimationHelpers.playAnimationUpgrade(this.heapOfStarsSpineElement)

        AnimationHelpers.playAnimationCollects(this.heapOfStarsSpineElement)
    }

    async handleTriggerBonusGame() {
        await this.playAnimationTrigger();
    }

    handleFlyFinishOutroCounter() {
        if (this.store.gameData.bonusGame.isPlaying()) return;

        this.runHardLandingArray(this.effectElements);
    }

    async playAnimationTrigger() {
        await HeapFeature.setDelayForShowingPreviousAnimation(this.heapFeatureData.TIME_HEAP_COLLECT_ANIMATION);

        this.runShakingArray(this.effectElements);
        AnimationHelpers.playAnimationWinHeap(this.heapOfStarsSpineElement)
        AnimationHelpers.playAnimationFalling(this.fallingStarsSpineElement);
    }

    playWinAnimationForAllCoins() {
        for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
            for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                const symbolElement = this.reelset.getReel(reelId).getSymbol(rowId);
                if (this.reelset.bonusGameConfig.coinSymbolIds.includes(symbolElement.currentSymbolId)) {
                    symbolElement.playWin();
                }
            }
        }
    }

    startSymbolsFlyOutroHeapAnimation(): Promise<void> {
        if (this.symbolsFlyOutOfHeapOfStar.length === 0) return;

        const firstSymbol = this.symbolsFlyOutOfHeapOfStar.shift();
        const symbol = this.reelset.getReel(firstSymbol.x).getSymbol(firstSymbol.y);
        const fromElement = this.containerCounterSpriteElement;
        const toElement = symbol;
        const pointOffsetX1Custom = toElement.reelId > 2 ? 350 : -350;
        let pointOffsetY1 = game.render.isPortraitMode ? (-700) : (-350)
        const ctx = this;
        const isLastSymbol = ctx.symbolsFlyOutOfHeapOfStar.length === 1;

        this.playSymbolFlyAnimation({
            time: this.heapFeatureData.TIME_MOVE_MAP.NORMAL_SPIN,
            fromElement: fromElement,
            toElement: toElement,
            pointOffsetX1: pointOffsetX1Custom,
            pointOffsetY1: pointOffsetY1,
            onStart: () => {
                game.emit(HeapFeature.events.FLY_START_OUTRO_COUNTER);
                if (isLastSymbol) {
                    game.emit(HeapFeature.events.FLY_START_LAST_OUTRO_COUNTER);
                }
            },
            onChange: () => { },
            onFinish: () => {
                game.emit(HeapFeature.events.FLY_FINISH_OUTRO_COUNTER);
            },
        });
    }

    async startSymbolsFlyIntroHeapAnimation(): Promise<void> {     
        const flySymbolArr = [];
        
        for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
            for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                const symbolData = this.store.gameData.reels[reelId][rowId];
                const symbolView = this.reelset.getReel(reelId).getSymbol(rowId);
                const isSymbolValid = this.heapFeatureData.bonusSymbolsId.includes(Number(symbolData?.symbol));
                
                if (isSymbolValid) {
                    flySymbolArr.push({ symbolData, symbolView });
                }
            }
        }
        
        const toElement = game.tree.findByName('logo_wrapper_container.logo_spine_container');
        await this.runSymbolFlyArray(flySymbolArr, toElement);
    }

    async playSymbolFlyAnimation({
        time,
        fromElement,
        fromElementOffset = { x: 0, y: 0 },
        toElement,
        toElementOffset = { x: 0, y: 0 },
        pointOffsetX1 = 0,
        pointOffsetY1 = 0,
        pointOffsetX2 = 0,
        pointOffsetY2 = 0,
        onStart,
        onChange,
        onFinish
    }) {
        const dust = this.addParticle("particleDustConfig")
        GameUtilsCustom.playCurveMoveAnimation(dust, {
            fromElement: fromElement,
            fromElementOffset: fromElementOffset,
            toElement: toElement,
            toElementOffset: toElementOffset,
            time: time,
            ease: "circIn",
            pointOffsetX1,
            pointOffsetY1,
            pointOffsetX2,
            pointOffsetY2,
            onStart: () => {
                onStart();

                dust.emitter.emit = true;
            },
            onChange: () => {
                onChange();
            },
            onFinish: () => {
                onFinish()
            }
        })

        const containerLittle_flying_star = game.tree.findByName("heapFeature.container_symbols")
        const littleFlyingStar = this.setOverlaySpine("little_flying_star", containerLittle_flying_star);

        await GameUtilsCustom.playCurveMoveAnimation(littleFlyingStar, {
            fromElement: fromElement,
            fromElementOffset: fromElementOffset,
            toElement: toElement,
            toElementOffset: toElementOffset,
            time: time,
            ease: "circIn",
            pointOffsetX1,
            pointOffsetY1,
            pointOffsetX2,
            pointOffsetY2,
            onStart: () => {
                onStart();
                littleFlyingStar.visible = true;
            },
            onChange: () => {
                onChange();
                AnimationHelpers.playRotationAnimation(littleFlyingStar, 0.2);
            },
            onFinish: async () => {
                onFinish();
            }
        });

        await game.utils.delay(0); //This is necessary for the correct sequence in the stack
        game.utils.PIXIUtils.remove(littleFlyingStar);
        game.utils.PIXIUtils.remove(dust);
    }

    async runSymbolFlyArray(flySymbolArr, toElement) {
        this.setVariableCurrentLevel();

        flySymbolArr.forEach(async (starItem, index) => {
            const pointOffsetX1Custom = starItem.symbolView.reelId > 2 ? 350 : -350;
            const pointOffsetY1 = -600;

            this.playSymbolFlyAnimation({
                time: this.heapFeatureData.currentTimeMove,
                fromElement: starItem.symbolView,
                toElement: toElement,
                pointOffsetX1: pointOffsetX1Custom,
                pointOffsetY1: pointOffsetY1,
                toElementOffset: {
                    x: 0,
                    y: 0,
                },
                onStart: () => {
                    const isLastFlyFinishIntroCounter = ((flySymbolArr.length - 1) === index);
                    if (isLastFlyFinishIntroCounter) {
                        game.emit(HeapFeature.events.FLY_START_LAST_INTRO_COUNTER);
                    }
                },
                onChange: () => { },
                onFinish: () => {
                    const isLastFlyFinishIntroCounter = ((flySymbolArr.length - 1) === index);
                    if (isLastFlyFinishIntroCounter) {
                        game.emit(HeapFeature.events.FLY_FINISH_LAST_INTRO_COUNTER);
                    }

                    const isLastFlyFinishIntroCounterRunHeapTriggerBonus = ((flySymbolArr.length - 1) === index) && this.isSpinHaveHeapTriggerBonus;
                    if (isLastFlyFinishIntroCounterRunHeapTriggerBonus) {
                        game.emit(HeapFeature.events.TRIGGER_BONUS_GAME);
                    }
                },
            });
        });
    }

    runHardLandingArray(elementsArray) {
        elementsArray.forEach((element) => AnimationHelpers.playHardLandingAnimation(
            element,
            {
                fromPositionY: element.options.position.y
            }
        ));
    }

    runShakingArray(elementsArray) {
        elementsArray.forEach((element) => AnimationHelpers.playShakingAnimation(
            element,
            {
                fromPositionX: element.options.position.x,
                fromPositionY: element.options.position.y
            }
        ));
    }

    removeSpriteLevel() {
        const isContainerDoesntHaveChildren = !this.containerCounterSpriteElement.children.length
        if (isContainerDoesntHaveChildren) return

        this.containerCounterSpriteElement.removeChildren();
    }

    addParticle(particleConfig): Particles {
        const particle = <Particles>game.builder.create(this.options.config[particleConfig]);

        this.view.addChildAt(particle, 0);

        return particle;
    }

    async onCustomReplaceSymbols() {
        this.store.applyReplaceSymbols();

        await HeapFeature.setDelayForShowingPreviousAnimation(HeapFeatureDataStore.TIME_HEAP_COLLECT_ANIMATION);

        for (let data of this.store.gameData.replace) {
            
            game.emit("GameReelsetComponent.SYMBOL_START_REPLACE");

            const timeWaitingCoinToFlyFinish = HeapFeatureDataStore.TIME_MOVE_MAP.NORMAL_SPIN;

            //let's write from "replace" in memory to build symbols in the bonus game
            this.store.gameData.reels[data.x][data.y].symbol = data.symbol;
            this.store.gameData.reels[data.x][data.y].value = data.value;

            game.once(HeapFeature.events.FLY_FINISH_OUTRO_COUNTER, async () => {
                //when the coin has landed, we start replacing the symbol
                this.reelset.replaceSymbol(data.x, data.y, data.symbol);
                this.reelset.getReel(data.x).getSymbol(data.y).playIdle();
            });

            await game.utils.delay(timeWaitingCoinToFlyFinish);
        }
    }

    playIdleAnimationForAllSymbols() {
        for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
            for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                const symbolElement = this.reelset.getReel(reelId).getSymbol(rowId);
                symbolElement.playIdle();
            }
        }
    }

    getFSMHandlers() {
        return {
            SPIN: {
                endSpin: {
                    on: async () => {
                        if (!this.isSpinHaveHeapTriggerBonus) return

                        this.symbolsFlyOutOfHeapOfStar = [...this.gameData.replace];
                    }
                },
                replaceSymbols: {
                    on: () => { }
                }
            },
            CHECK: {
                flyStarsIntoHeapTriggerBonusGame: {
                    on: async () => {
                        if (this.heapFeatureData.isSpinHaveWin) {
                            this.playIdleAnimationForAllSymbols();
                        }
                        this.startSymbolsFlyIntroHeapAnimation();
                        await HeapFeature.setDelayForShowingPreviousAnimation(this.heapFeatureData.TIME_COMMON_SHAKING_ANIMATION, this.heapFeatureData.currentTimeMove);
                    },
                    check: () => this.isSpinHaveHeapTriggerBonus
                },
                flyStarsIntroHeapBeforeBonusWin: {
                    on: async () => {
                        this.startSymbolsFlyIntroHeapAnimation();
                    },
                    check: () => this.heapFeatureData.isCanFlyStarsIntroHeapBeforeBonusWin
                },
                flyStarsIntroHeapBeforeStartFreeSpinsGame: {
                    on: async () => {
                        this.playIdleAnimationForAllSymbols();

                        this.startSymbolsFlyIntroHeapAnimation();
                        await HeapFeature.setDelayForShowingPreviousAnimation(this.heapFeatureData.currentTimeMove);
                    },
                    check: () => this.heapFeatureData.isCanFlyStarsIntroHeapBeforeStartFreeSpinsGame
                },
                customReplaceSymbols: {
                    on: async () => {
                        await this.onCustomReplaceSymbols();
                    },
                    check: () => this.isSpinHaveHeapTriggerBonus
                },
                checkFinishBonusGame: {
                    on: () => {
                        this.setVariableCurrentLevel()
                    }
                },
                setDelayForShowFlyStarInFinishFreeSpinsGame: {
                    on: async () => {
                        this.playIdleAnimationForAllSymbols();

                        await HeapFeature.setDelayForShowingPreviousAnimation(this.heapFeatureData.currentTimeMove, this.heapFeatureData.TIME_HEAP_COLLECT_ANIMATION);
                    },
                    check: () => this.heapFeatureData.isShowFlyStarInFinishFreeSpinsGame()
                },
                flyStarsIntroHeap: {
                    on: async () => {
                        this.playIdleAnimationForAllSymbols();
                        this.startSymbolsFlyIntroHeapAnimation();
                        
                        this.setVariablePreviousLevel();
                    },
                    check: () => this.heapFeatureData.isCanRunFlyIntoHeap
                }
            }
        }
    }

    static async setDelayForShowingPreviousAnimation(...times) {
        const sumOfTimes = times.reduce((total, time) => total + time, 0);
        await game.utils.delay(sumOfTimes);
    }

    setCurrentLevelSprite(level) {
        const currentSprite = this.heapFeatureData.getCurrentLevelSprite(level);
        this.containerCounterSpriteElement.addChild(currentSprite);
    }

    setVariableCurrentLevel() {
        this.heapFeatureData.currentLevel = this.heapOfStarLevelValue;
    }

    setVariablePreviousLevel() {
        this.heapFeatureData.previousLevel = this.heapOfStarLevelValue;
    }

    setElementVisibility(element, isVisible) {
        if (!element) return;
        if (element.options.visible !== isVisible) {
            element.options.visible = isVisible;
            element.updateViewOptions();
        }
    }

    setOverlaySpine(textureName, view) {
        const overlayView: any = game.builder.create(
            {
                type: "Sprite",
                texture: textureName,
                parentGroup: "OVERLAY",
                zOrder: 11
            }
        );
        overlayView.options.visible = false;
        overlayView.updateViewOptions();
        view.addChild(overlayView);

        return overlayView;
    }

    get store(): SlotGameStore {
        return <SlotGameStore>game.store;
    }

    get gameData(): GameDataStore {
        return (<GameDataStore>this.store.gameData);
    }

    get heapFeatureData() {
        return HeapFeatureDataStore;
    }

    get isSpinHaveHeapTriggerBonus() {
        return !!this.gameData.replace.length
    }

    get heapOfStarLevelValue() {
        return this.gameData.extra.heapOfGoldLevel;
    }
}

export { HeapFeature };

//@ts-ignore
Game.src.component.HeapFeature = HeapFeature;